import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { identityServices } from '../identityServices'
import {
  VerifyPhoneUpdatePayload,
  VerifyPhoneUpdateDTO,
} from '../identityTypes'
import { ApiErrorCode, ErrorDetail } from '@/api/apiTypes'
import { mapAxiosErrorToApiError } from '@/utils'

interface UseVerifyPhoneUpdateOptions {
  onSuccess?: (data: VerifyPhoneUpdateDTO) => void
  onError?: (error: { errorMessage?: string; code?: string }) => void
}

export const useVerifyPhoneUpdate = (options?: UseVerifyPhoneUpdateOptions) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  return useMutation<
    VerifyPhoneUpdateDTO,
    ErrorDetail,
    VerifyPhoneUpdatePayload
  >({
    mutationFn: identityServices.verifyPhoneUpdate,
    onSuccess: (data) => {
      // Refresh user data after successful phone verification
      queryClient.invalidateQueries({ queryKey: ['identity', 'me'] })

      options?.onSuccess?.(data)
    },
    onError: (err) => {
      const { code } = mapAxiosErrorToApiError(err)

      let errorMessage = t('api:errors.somethingWrong')

      if (code === ApiErrorCode.TOO_MANY_ATTEMPTS) {
        errorMessage = t('api:errors.manyAttempts')
      }

      if (code === ApiErrorCode.CODE_NOT_VERIFIED || code === 'CODE_INVALID') {
        errorMessage = t('api:validationErrors.invalidCode')
      }

      if (code === ApiErrorCode.PHONE_NUMBER_ALREADY_EXISTS_ERROR) {
        errorMessage = t('api:phoneNumberAlreadyInUse')
      }

      options?.onError?.({
        errorMessage,
        code,
      })
    },
  })
}
